<template>
  <p class="py-3 text-sm font-light">
    {{ $t('Forgot Password') }}
  </p>
  <form
    class="mb-20"
    @submit="onSubmit"
  >
    <div class="mb-3">
      <KikLabel
        label-for="email"
        :label="$t('Forgot Password Modal Email')"
      >
        <SfInput
          id="email"
          v-model="email"
          :invalid="!!errors.email"
          name="email"
          type="email"
          data-testid="forgot-password__email-input"
          :placeholder="$t('Forgot Password Modal Email')"
          v-bind="emailAttrs"
        />
      </KikLabel>

      <KikFormError
        class="mt-1"
        :error="errors.email"
      />
    </div>
    <p
      v-if="forgotPasswordError"
      class="mt-3 text-sm font-light text-primary-700"
      data-testid="forgot-password__error-message"
    >
      {{ $t("forgotPasswordMailError") }}
    </p>
    <SfLink
      class="float-right mt-2 block cursor-pointer text-sm underline"
      variant="primary"
      data-testid="forgot-password__login-link"
      tabindex="0"
      @keydown.enter="$emit('changeView', 'login')"
      @click="$emit('changeView', 'login')"
    >
      {{ $t('login in to your account') }}
    </SfLink>

    <div class="absolute inset-x-0 -bottom-6 me-auto ms-auto w-[320px]">
      <div class="bg-neutral-50 text-center">
        <KikButton
          class="h-[48px] min-w-[280px]"
          variant="primary"
          :disabled="isSubmitting"
          data-testid="forgot-password__submit-btn"
          v-bind="{ type: 'submit' }"
        >
          <SfLoaderCircular v-if="isSubmitting" />
          <div v-else>
            {{ $t('Submit') }}
          </div>
        </KikButton>
      </div>
    </div>
  </form>
</template>

<script setup lang="ts">
import { SfInput, SfLink, SfLoaderCircular } from '@storefront-ui/vue';
import * as zod from 'zod';

import { useBFF } from '~/composables/useBFF/useBFF';

interface Props {
  userEmail?: string;
}

const props = withDefaults(defineProps<Props>(), {
  userEmail: '',
});

const emit = defineEmits(['changeView']);

const { $me } = useBFF();

const { defineField, isSubmitting, errors, handleSubmit } = useForm({
  validationSchema: toTypedSchema(
    zod.object({
      email: zod.string({ message: 'This field is required.' }).min(1, { message: 'This field is required.' }).email({ message: 'The e-mail address is incorrect.' }).default(props.userEmail),
    }),
  ),
});

const [email, emailAttrs] = defineField('email');

const forgotPasswordError = shallowRef();

const onSubmit = handleSubmit(async (values) => {
  forgotPasswordError.value = null;
  try {
    await $me.requestPassword(values.email);
  }
  catch (err: unknown) {
    forgotPasswordError.value = err;
  }

  if (!forgotPasswordError.value) {
    emit('changeView', 'thankYou', values.email);
  }
});
</script>
